import gql from 'graphql-tag';

import { Mutation, Query } from 'core/index';

export const GROUP_PROFILE_FIELDS = gql`
  fragment groupProfileFields on Profile {
    id
    username
    fullName
    imageFallbackChar
    user {
      id
      username
    }
    avatar {
      id
      path
      url
      completed
    }
  }
`;

export const GROUP_FIELDS = gql`
  ${GROUP_PROFILE_FIELDS}
  fragment groupFields on Group {
    id
    name
    canUpdate
    canDelete
    canAddMember
    space {
      id
    }
    parent {
      id
      name
    }
    pendingInvites {
      id
      inviteDate
      email
      status
      profile {
        ...groupProfileFields
      }
      group {
        id
        spaceId
        name
      }
    }
    members {
      id
      canUpdate
      canDelete
      intrinsic
      profile {
        ...groupProfileFields
      }
    }
  }
`;

const GROUPS_QUERY = gql`
  ${GROUP_FIELDS}
  query Groups($spaceSlug: String, $slug: String) {
    groups(spaceSlug: $spaceSlug, slug: $slug) {
      ...groupFields
    }
  }
`;
export class GroupsQuery extends Query {
  static conf = {
    GQL: GROUPS_QUERY,
    varNames: ['spaceSlug', 'slug'],
    resultPath: 'groups',
  };
}

const INVITE_USERS_TO_GROUP = gql`
  ${GROUP_FIELDS}
  mutation InviteUsersToGroup($userGroupInput: UserGroupInviteInput) {
    inviteUsersToGroup(userGroupInput: $userGroupInput) {
      group {
        ...groupFields
      }
    }
  }
`;

export class InviteUsersToGroup extends Mutation {
  static conf = {
    GQL: INVITE_USERS_TO_GROUP,
    inputName: 'userGroupInput',
    resultPath: 'inviteUsersToGroup.group',
  };
}

const REMOVE_USER_FROM_GROUP = gql`
  ${GROUP_FIELDS}
  mutation RemoveUserFromGroup($userGroupInput: UserGroupActionInput) {
    removeUserFromGroup(userGroupInput: $userGroupInput) {
      group {
        ...groupFields
      }
    }
  }
`;
export class RemoveUserFromGroup extends Mutation {
  static conf = {
    GQL: REMOVE_USER_FROM_GROUP,
    inputName: 'userGroupInput',
    resultPath: 'removeUserFromGroup.group',
  };
}

const MOVE_USER_TO_GROUP = gql`
  ${GROUP_FIELDS}
  mutation MoveGroupMember($moveGroupMemberInput: MoveGroupMemberInput) {
    moveGroupMember(moveGroupMemberInput: $moveGroupMemberInput) {
      result
      fromGroup {
        ...groupFields
      }
      toGroup {
        ...groupFields
      }
    }
  }
`;

export class MoveUserToGroup extends Mutation {
  static conf = {
    GQL: MOVE_USER_TO_GROUP,
    inputName: 'moveGroupMemberInput',
    resultPath: 'moveGroupMember',
  };
}

const REMOVE_INVITE = gql`
  ${GROUP_FIELDS}
  mutation DeleteInvite($data: DeleteInviteInput) {
    deleteInvite(data: $data) {
      group {
        ...groupFields
      }
    }
  }
`;

export class RemoveInvite extends Mutation {
  static conf = {
    GQL: REMOVE_INVITE,
    inputName: 'data',
    resultPath: 'deleteInvite.group',
  };
}

const INVITE_USER_TO_SIGNUP = gql`
  mutation InviteUsersToSignup($newInvitesInput: SignupInviteInput) {
    inviteUsersToSignup(userGroupInput: $newInvitesInput) {
      result
      invites {
        id
        status
        email
      }
      invitesLeft
    }
  }
`;

const MOVE_INVITE_TO_GROUP = gql`
  ${GROUP_FIELDS}
  mutation MoveGroupInvite($moveGroupInviteInput: MoveGroupInviteInput) {
    moveGroupInvite(moveGroupInviteInput: $moveGroupInviteInput) {
      result
      fromGroup {
        ...groupFields
      }
      toGroup {
        ...groupFields
      }
    }
  }
`;

const REMOVE_OUTSIDE_COLLABORATORS = gql`
  mutation RemoveOutsideCollaborator(
    $removeOutsideCollaboratorInput: RemoveOutsideCollaboratorInput
  ) {
    removeOutsideCollaborator(
      removeOutsideCollaboratorInput: $removeOutsideCollaboratorInput
    ) {
      result
    }
  }
`;

export class RemoveOutsideCollaborator extends Mutation {
  static conf = {
    GQL: REMOVE_OUTSIDE_COLLABORATORS,
    inputName: 'removeOutsideCollaboratorInput',
  };
}

export class MoveInviteToOtherGroup extends Mutation {
  static conf = {
    GQL: MOVE_INVITE_TO_GROUP,
    inputName: 'moveGroupInviteInput',
    resultPath: 'moveGroupInvite',
  };
}

export class InviteUserToSignUpMutation extends Mutation {
  static conf = {
    GQL: INVITE_USER_TO_SIGNUP,
    inputName: 'newInvitesInput',
  };
}

const SIGN_UP_INVITES = gql`
  query SignUpInvites {
    signupInvites {
      id
      status
      email
      moreInvitesRequested
      invitesLeft
    }
  }
`;

export class SignUpInvitesQuery extends Query {
  static conf = {
    GQL: SIGN_UP_INVITES,
    resultPath: 'signupInvites',
  };
}

const REQUEST_MORE_INVITES = gql`
  mutation RequestMoreInvites($requestInvitesInput: InvitesRequestInput) {
    requestMoreInvites(requestInput: $requestInvitesInput) {
      result
    }
  }
`;

export class RequestInvitesMutation extends Mutation {
  static conf = {
    GQL: REQUEST_MORE_INVITES,
    inputName: 'requestInvitesInput',
  };
}

const SIGN_UP_REQUEST = gql`
  mutation SignUpRequest($inviteRequestInput: SignupRequestInput) {
    requestInvite(requestInput: $inviteRequestInput) {
      result
    }
  }
`;

export class SignUpRequestMutation extends Mutation {
  static conf = {
    GQL: SIGN_UP_REQUEST,
    inputName: 'inviteRequestInput',
  };
}

const ACCEPT_INVITE = gql`
  mutation AcceptInvite($data: AcceptInviteInput) {
    acceptInvite(data: $data) {
      result
    }
  }
`;

export class AcceptInvite extends Mutation {
  static conf = {
    GQL: ACCEPT_INVITE,
    inputName: 'data',
  };
}

const REJECT_INVITE = gql`
  mutation RejectInvite($data: RejectInviteInput) {
    rejectInvite(data: $data) {
      result
    }
  }
`;

export class RejectInvite extends Mutation {
  static conf = {
    GQL: REJECT_INVITE,
    inputName: 'data',
  };
}

const INITIATIVE_INVITE_LINK = gql`
  query InitiativeInviteLink($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      id
      inviteLink {
        id
        url
        expiresAt
      }
    }
  }
`;

export const PROFILE_SEATS = gql`
  query ProfileSeats($username: String!) {
    profile(username: $username) {
      id
      collaborators {
        username
      }
      hasProSubscription
      seatsUsage {
        unusedSeats
      }
    }
  }
`;

export const PROFILE_SEATS_WITHOUT_COLLABORATORS = gql`
  query ProfileSeatsWithoutCollaborators($username: String!) {
    profile(username: $username) {
      id
      seatsUsage {
        unusedSeats
      }
    }
  }
`;

export class ProfileSeatsQuery extends Query {
  static conf = {
    GQL: PROFILE_SEATS,
    varNames: ['username'],
    resultPath: 'profile',
  };
}

export class ProfileSeatsWithoutCollaboratorsQuery extends Query {
  static conf = {
    GQL: PROFILE_SEATS_WITHOUT_COLLABORATORS,
    varNames: ['username'],
    resultPath: 'profile',
  };
}

export const INITIATIVE_SEATS = gql`
  query InitiativeSeats($slug: String) {
    initiative(slug: $slug) {
      id
      collaborators {
        id
        username
        profile {
          id
          fullName
          username
          avatar {
            url
          }
          imageFallbackChar
        }
      }
      seatsUsage {
        unusedSeats
      }
    }
  }
`;

export const INITIATIVE_SEATS_WITHOUT_COLLABORATORS = gql`
  query InitiativeSeats($slug: String) {
    initiative(slug: $slug) {
      id
      seatsUsage {
        unusedSeats
      }
    }
  }
`;

export class InitiativeSeatsQuery extends Query {
  static conf = {
    GQL: INITIATIVE_SEATS,
    varNames: ['slug'],
    resultPath: 'initiative',
  };
}

export class InitiativeSeatsWithoutCollaboratorsQuery extends Query {
  static conf = {
    GQL: INITIATIVE_SEATS_WITHOUT_COLLABORATORS,
    varNames: ['slug'],
    resultPath: 'initiative',
  };
}

export class InitiativeInvLinkQuery extends Query {
  static conf = {
    GQL: INITIATIVE_INVITE_LINK,
    varNames: ['spaceSlug', 'slug'],
    resultPath: 'initiative.inviteLink',
  };
}

const PROJECT_INVITE_LINK = gql`
  query ProjectInviteLink($spaceSlug: String, $slug: String) {
    project(space: $spaceSlug, slug: $slug) {
      id
      inviteLink {
        id
        url
        expiresAt
      }
    }
  }
`;

export class ProjectInvLinkQuery extends Query {
  static conf = {
    GQL: PROJECT_INVITE_LINK,
    varNames: ['spaceSlug', 'slug'],
    resultPath: 'project.inviteLink',
  };
}

const CREATE_INVITE_LINK = gql`
  mutation CreateInviteLink($data: CreateInviteLinkInput) {
    createInviteLink(data: $data) {
      inviteLink {
        id
        url
        expiresAt
      }
    }
  }
`;

export class CreateInviteLink extends Mutation {
  static conf = {
    GQL: CREATE_INVITE_LINK,
    inputName: 'data',
  };
}

const DELETE_INVITE_LINK = gql`
  mutation DeleteInviteLink($data: DeleteInviteLinkInput) {
    deleteInviteLink(data: $data) {
      result
    }
  }
`;

export class DeleteInviteLink extends Mutation {
  static conf = {
    GQL: DELETE_INVITE_LINK,
    inputName: 'data',
  };
}

const INVITE_QUERY = gql`
  query InviteLink(
    $token: String!
    $inviteLinkId: String!
    $referrerUserId: String!
  ) {
    inviteLink(
      token: $token
      inviteLinkId: $inviteLinkId
      referrerUserId: $referrerUserId
    ) {
      id
      content {
        id
        title
        description
        avatar {
          id
          path
          url
        }
        inSpace {
          id
          spaceType
          parent {
            id
            spaceType
            content {
              id
              slug
            }
          }
        }
        slug
        parentSlug
        isPrivate
        imageFallbackChar
        type
      }
    }
  }
`;

export class InviteQuery extends Query {
  static conf = {
    GQL: INVITE_QUERY,
    varNames: ['token', 'inviteLinkId', 'referrerUserId'],
    resultPath: 'inviteLink',
  };
}
